/* Renders lesson blocks (see src/content/lessons/blocks.ts for the block types). */
import { type ReactNode, useState } from 'react';
import { useGo } from '../../app/navigation';
import type { Block, LabeledSpec, TableCell } from '../../content/lessons/blocks';
import { audio, type PlaySpec } from '../../core/audio';
import { parseNote, parsePc, positions, shortNote } from '../../core/theory';
import { Btn, PlayButton, SpecButton } from '../../ui/Button';
import { ChordDiagram } from '../../ui/ChordDiagram';
import { ErrorBoundary } from '../../ui/ErrorBoundary';
import { Fretboard, type FretMark, pcMarks } from '../../ui/Fretboard';
import { Icon } from '../../ui/icons';
import { Keyboard, type KeyMarks } from '../../ui/Keyboard';
import { NeckScale } from '../../ui/NeckScale';
import { Rich, richText } from '../../ui/Rich';
import { BreathWidget } from './BreathWidget';

const SPEC_KEYS = ['seq', 'harm', 'glide', 'chord', 'prog', 'cadence'] as const;

export function specOf(o: PlaySpec): PlaySpec {
  const spec: PlaySpec = {};
  for (const k of SPEC_KEYS) if (o[k] != null) spec[k] = o[k];
  if (o.bpm) spec.bpm = o.bpm;
  if (o.inst) spec.inst = o.inst;
  if (o.beats) spec.beats = o.beats;
  if (o.pattern) spec.pattern = o.pattern;
  return spec;
}

export const hasSpec = (o: unknown): o is PlaySpec =>
  !!o && typeof o === 'object' && SPEC_KEYS.some((k) => (o as PlaySpec)[k] != null);

/** `row`: the first cell of the row, which names an icon-only play button. */
function Cell({ c, head, row }: { c: TableCell; head: boolean; row?: TableCell }) {
  let content: ReactNode;
  const name = typeof row === 'string' || typeof row === 'number' ? richText(String(row)) : undefined;
  if (c != null && typeof c === 'object' && 'play' in c && hasSpec(c.play))
    content = (
      <PlayButton label={c.label || ''} name={name} start={() => audio.playSpec(specOf(c.play))} className="btn-sm" />
    );
  else if (c != null && typeof c === 'object' && hasSpec(c))
    content = <SpecButton label={(c as LabeledSpec).label || ''} spec={specOf(c)} className="btn-sm" />;
  else content = <Rich text={c == null ? '' : String(c)} />;
  return head ? <th>{content}</th> : <td>{content}</td>;
}

function Quiz({ b }: { b: Extract<Block, { t: 'quiz' }> }) {
  const [picked, setPicked] = useState<Record<number, 'ok' | 'bad'>>({});
  const [exp, setExp] = useState<{ ok: boolean } | null>(null);
  const answered = exp?.ok === true;
  return (
    <div className="quiz">
      <div className="quiz-q">
        <Icon name="bulb" />
        <span>
          <Rich text={b.q} />
        </span>
      </div>
      {hasSpec(b.play) ? (
        <div className="quiz-play">
          <SpecButton label="Dinle" spec={specOf(b.play)} />
        </div>
      ) : null}
      {b.items?.length ? (
        <div className="quiz-play">
          {b.items.map((it, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static content in fixed order
            <SpecButton key={i} label={it.label || `${i + 1}. ses`} spec={specOf(it)} />
          ))}
        </div>
      ) : null}
      <div className="quiz-opts">
        {b.options.map((o, i) => (
          <button
            key={o}
            type="button"
            className={`quiz-opt${picked[i] ? ` ${picked[i]}` : ''}`}
            disabled={picked[i] === 'bad'}
            onClick={() => {
              if (answered) return;
              if (i === b.answer) {
                setPicked((p) => ({ ...p, [i]: 'ok' }));
                setExp({ ok: true });
              } else {
                setPicked((p) => ({ ...p, [i]: 'bad' }));
                setExp({ ok: false });
              }
            }}
          >
            <Rich text={o} inert />
          </button>
        ))}
      </div>
      {exp ? (
        <div className={`quiz-exp ${exp.ok ? 't-good' : 't-bad'}`}>
          {exp.ok ? (
            <>
              <strong>Doğru. </strong>
              <Rich text={b.explain || ''} />
            </>
          ) : (
            <>
              <strong>Tekrar dene. </strong>
              {b.hint ? <Rich text={b.hint} /> : 'Bir kez daha dinle.'}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function FretboardBlock({ b }: { b: Extract<Block, { t: 'fretboard' }> }) {
  const from = b.from || 0;
  const to = b.to == null ? 12 : b.to;
  let marks: FretMark[] = [];
  if (b.notes) {
    const ms = b.notes
      .trim()
      .split(/\s+/)
      .map((x) => parseNote(x))
      .filter((x): x is number => x != null);
    for (const m of ms)
      for (const p of positions(m, to))
        if (p.f >= from) marks.push({ s: p.s, f: p.f, cls: 'm-note', label: shortNote(m, { octave: false }) });
  }
  if (b.pcs) {
    const pcs = b.pcs
      .trim()
      .split(/\s+/)
      .map((x) => parsePc(x))
      .filter((x): x is number => x != null);
    const root = b.root ? parsePc(b.root) : null;
    marks = marks.concat(pcMarks(pcs, { from, to, root }));
  }
  return (
    <figure className="lesson-fig">
      <Fretboard
        from={from}
        to={to}
        marks={marks}
        onPick={(_s, _f, m) => {
          audio.resume();
          audio.playNote(m, { inst: 'guitar', dur: 1.2 });
        }}
      />
      <figcaption>
        <Rich text={b.caption || 'Bir perdeye dokunarak sesini dinleyebilirsin.'} />
      </figcaption>
    </figure>
  );
}

function KeyboardBlock({ b }: { b: Extract<Block, { t: 'keyboard' }> }) {
  const from = parseNote(b.from || 'C4') ?? 60;
  const to = parseNote(b.to || 'C5') ?? 72;
  const marks: KeyMarks = {};
  for (const x of (b.highlight || '').trim().split(/\s+/)) {
    const m = parseNote(x);
    if (m != null) marks[m] = { cls: 'm-note', label: shortNote(m, { octave: false }) };
  }
  return (
    <figure className="lesson-fig">
      <Keyboard
        from={from}
        to={to}
        marks={marks}
        labels="c"
        onPick={(m) => {
          audio.resume();
          audio.playNote(m, { inst: 'piano', dur: 1.2 });
        }}
      />
      {b.caption ? (
        <figcaption>
          <Rich text={b.caption} />
        </figcaption>
      ) : null}
    </figure>
  );
}

function NeckBlock({ b }: { b: Extract<Block, { t: 'neck' }> }) {
  return (
    <figure className="lesson-fig">
      <NeckScale
        frets={b.frets}
        string={b.string}
        onPick={(_f, m) => {
          audio.resume();
          audio.playNote(m, { inst: 'guitar', dur: 1.2 });
        }}
      />
      <figcaption>
        <Rich
          text={b.caption || 'Perdeler gerçek oranlarıyla çizildi. Bir perdeye dokunarak sesini dinleyebilirsin.'}
        />
      </figcaption>
    </figure>
  );
}

function GoBlock({ b }: { b: Extract<Block, { t: 'go' }> }) {
  const go = useGo();
  return (
    <div className="lesson-go">
      <Btn kind="primary" icon="target" onClick={() => go(b.route)}>
        {richText(b.label)}
      </Btn>
    </div>
  );
}

export function BlockView({ b }: { b: Block }) {
  switch (b.t) {
    case 'p':
      return (
        <p>
          <Rich text={b.text} />
        </p>
      );
    case 'h':
      return <h3 className="lesson-h">{b.text}</h3>;
    case 'list': {
      const items = b.items.map((it) => (
        <li key={it}>
          <Rich text={it} />
        </li>
      ));
      return b.ordered ? <ol className="lesson-list">{items}</ol> : <ul className="lesson-list">{items}</ul>;
    }
    case 'tip':
      return (
        <aside className={`tip t-${b.tone || 'info'}`}>
          <Icon name={b.tone === 'warn' ? 'alert' : b.tone === 'success' ? 'check' : 'bulb'} />
          <div>
            {b.title ? (
              <strong className="tip-title">
                <Rich text={b.title} />
              </strong>
            ) : null}
            <p>
              <Rich text={b.text} />
            </p>
          </div>
        </aside>
      );
    case 'steps':
      return (
        <ol className="steps">
          {b.items.map((it) => (
            <li key={it.title}>
              <strong>
                <Rich text={it.title} />
              </strong>
              <p>
                <Rich text={it.text} />
              </p>
            </li>
          ))}
        </ol>
      );
    case 'play':
      return (
        <div className="lesson-play">
          <SpecButton label={b.label || 'Dinle'} spec={specOf(b)} />
        </div>
      );
    case 'compare':
      return (
        <div className="lesson-compare">
          {b.items.map((it, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static content in fixed order
            <SpecButton key={i} label={it.label || 'Dinle'} spec={specOf(it)} />
          ))}
        </div>
      );
    case 'table':
      return (
        <div className="table-wrap">
          <table className="lesson-table">
            {b.head ? (
              <thead>
                <tr>
                  {b.head.map((c, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: table cells are positional
                    <Cell key={i} c={c} head />
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {b.rows.map((r, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: table rows are positional
                <tr key={i}>
                  {r.map((c, j) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: table cells are positional
                    <Cell key={j} c={c} head={false} row={r[0]} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'fretboard':
      return <FretboardBlock b={b} />;
    case 'keyboard':
      return <KeyboardBlock b={b} />;
    case 'neck':
      return <NeckBlock b={b} />;
    case 'chords':
      return (
        <figure className="lesson-fig">
          <div className="chord-row">
            {b.names
              .trim()
              .split(/\s+/)
              .map((n) => (
                <ChordDiagram key={n} chord={n} />
              ))}
          </div>
          <figcaption>
            <Rich text={b.caption || 'Şemaya dokunarak akoru dinleyebilirsin.'} />
          </figcaption>
        </figure>
      );
    case 'quiz':
      return <Quiz b={b} />;
    case 'breath':
      return <BreathWidget pattern={b.pattern || [4, 4, 8]} rounds={b.rounds || 3} />;
    case 'go':
      return <GoBlock b={b} />;
    default:
      return null;
  }
}

export function LessonBlocks({ blocks }: { blocks: readonly Block[] }) {
  return (
    <div className="lesson-body">
      {blocks.map((b, i) => (
        // a broken block is skipped instead of breaking the lesson
        // biome-ignore lint/suspicious/noArrayIndexKey: blocks are static content in fixed order
        <ErrorBoundary key={i}>
          <BlockView b={b} />
        </ErrorBoundary>
      ))}
    </div>
  );
}
